import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '../supabase';
import { 
  Phone, MapPin, Home, Building2, 
  ChevronRight, ChevronLeft, ArrowLeft,
  CheckCircle2, Circle, Users, User,
  Activity, UserPlus, X, Calendar, Clock
} from 'lucide-react';
import Navbar from '../components/Navbar';
import './BookAppointment.css';

const STEPS = [
  { id: 1, title: 'Phone' },
  { id: 2, title: 'Address' },
  { id: 3, title: 'People' },
  { id: 4, title: 'Schedule' },
  { id: 5, title: 'Confirm' }
];

function BookAppointment() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(0);
  const [formData, setFormData] = useState({
    phone: '',
    address: {
      towerNo: '',
      flatNo: '',
      apartmentName: ''
    },
    individuals: [
      { id: Date.now(), name: '', age: '', gender: 'Male' }
    ],
    notes: '',
    preferred_date: '',
    slot_time: ''
  });

  const [doctorInfo, setDoctorInfo] = useState(null);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [bookingRef, setBookingRef] = useState(null);

  const DEFAULT_DOCTOR_INFO = {
    name: 'Gaurav Singh',
    available_days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
    booking_slots: [{ "start": "10:00", "end": "20:00" }]
  };

  // Fetch doctor availability on mount
  useEffect(() => {
    async function getDoctorAvailability() {
      try {
        // Find a doctor who has booking_slots configured
        const { data, error } = await supabase
          .from('users')
          .select('id, available_days, booking_slots, name, clinic_id, role')
          .eq('role', 'doctor')
          .not('booking_slots', 'is', null)
          .limit(1);
        
        if (!error && data && data.length > 0) {
          setDoctorInfo(data[0]);
        } else {
          console.warn('[BookAppointment] No doctor with booking_slots found. Using defaults.');
          setDoctorInfo(DEFAULT_DOCTOR_INFO);
        }
      } catch (err) {
        console.error('[BookAppointment] Error fetching availability:', err);
        setDoctorInfo(DEFAULT_DOCTOR_INFO);
      }
    }
    getDoctorAvailability();
  }, []);

  // Fetch booked slots when date changes
  useEffect(() => {
    if (formData.preferred_date) {
      async function getBookedSlots() {
        const { data, error } = await supabase
          .from('appointments')
          .select('slot_time')
          .eq('preferred_date', formData.preferred_date)
          .neq('status', 'cancelled');
        
        if (!error && data) {
          setBookedSlots(data.map(d => d.slot_time));
        }
      }
      getBookedSlots();
    }
  }, [formData.preferred_date]);

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setFormData(prev => ({ ...prev, phone: value }));
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [name]: value
      }
    }));
  };

  const handleIndividualChange = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      individuals: prev.individuals.map(ind => 
        ind.id === id ? { ...ind, [field]: value } : ind
      )
    }));
  };

  const addIndividual = () => {
    if (formData.individuals.length >= 10) {
      toast.error('Maximum 10 people allowed per booking');
      return;
    }
    setFormData(prev => ({
      ...prev,
      individuals: [
        ...prev.individuals,
        { id: Date.now(), name: '', age: '', gender: 'Male' }
      ]
    }));
  };

  const removeIndividual = (id) => {
    if (formData.individuals.length <= 1) return;
    setFormData(prev => ({
      ...prev,
      individuals: prev.individuals.filter(ind => ind.id !== id)
    }));
  };

  const nextStep = () => {
    if (step === 1) {
      if (formData.phone.length !== 10) {
        toast.error('Please enter a valid 10-digit phone number');
        return;
      }
    } else if (step === 2) {
      const { towerNo, flatNo, apartmentName } = formData.address;
      if (!towerNo || !flatNo || !apartmentName) {
        toast.error('Please fill in all required fields');
        return;
      }
    } else if (step === 3) {
      const invalid = formData.individuals.some(ind => !ind.name || !ind.age);
      if (invalid) {
        toast.error('Please fill in name and age for all individuals');
        return;
      }
    } else if (step === 4) {
      if (!formData.preferred_date || !formData.slot_time) {
        toast.error('Please select both date and time slot');
        return;
      }
    }
    
    if (step < 5) {
      setDirection(1);
      setStep(prev => prev + 1);
    }
  };

  const generateBookingRef = () => {
    const today = new Date();
    const dateStr = today.toISOString().slice(0, 10).replace(/-/g, '');
    const random = Math.random().toString(36).substring(2, 6).toUpperCase();
    return `KC-${dateStr}-${random}`;
  };

  const handleConfirmBooking = async () => {
    if (!doctorInfo) {
      toast.error('Doctor information missing. Please try again.');
      return;
    }

    setIsSubmitting(true);
    const ref = generateBookingRef();

    try {
      const { data, error } = await supabase
        .from('appointments')
        .insert({
          booking_ref: ref,
          clinic_id: doctorInfo.clinic_id || '0a6fc1b3-56ab-4f4c-acd4-c8076feb4c37',
          doctor_id: doctorInfo.id,
          patient_phone: formData.phone,
          appointment_type: 'medical_certificate',
          address_line1: `${formData.address.towerNo}, ${formData.address.flatNo}`,
          address_line2: formData.address.apartmentName || null,
          landmark: null,
          city: 'Noida',
          pincode: null,
          preferred_date: formData.preferred_date,
          slot_time: formData.slot_time,
          notes: formData.notes || null,
          status: 'pending',
          individuals: formData.individuals
        })
        .select()
        .single();

      if (error) {
        if (error.code === '23505') {
          toast.error('This time slot was just booked by someone else. Please select a different slot.');
          setStep(4);
          return;
        }
        throw error;
      }

      setBookingRef(ref);
      setIsSuccess(true);
      toast.success('Appointment booked successfully!');
    } catch (err) {
      console.error('Booking error:', err);
      toast.error('Failed to confirm booking. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatFullDate = (dateStr) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const prevStep = () => {
    if (step === 1) {
      navigate('/');
    } else {
      setDirection(-1);
      setStep(prev => prev - 1);
    }
  };

  // Helper to get YYYY-MM-DD for local date
  const getLocalDateString = (date) => {
    if (!date) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  // Calendar Logic
  const generateCalendarDays = () => {
    const start = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const end = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    const days = [];
    
    // Offset for Monday-start: Mon=0, Tue=1 ... Sun=6
    // getDay() gives Sun=0, Mon=1, Tue=2...
    const dayOfWeek = start.getDay();
    const offset = (dayOfWeek + 6) % 7;
    
    for (let i = 0; i < offset; i++) {
      days.push(null);
    }
    
    for (let i = 1; i <= end.getDate(); i++) {
      days.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i));
    }
    
    return days;
  };

  const isDayAvailable = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0,0,0,0);
    
    const dateCopy = new Date(date);
    dateCopy.setHours(0,0,0,0);
    
    // Check if date is in past or more than 30 days away
    if (dateCopy < today) return false;
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(today.getDate() + 30);
    if (dateCopy > thirtyDaysFromNow) return false;
    
    // Check if doctor works on this day
    if (!doctorInfo?.available_days || !Array.isArray(doctorInfo.available_days)) return true;
    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    return doctorInfo.available_days.some(d => d.trim().toLowerCase() === dayName);
  };

  const isDaySelected = (date) => {
    if (!date || !formData.preferred_date) return false;
    return getLocalDateString(date) === formData.preferred_date;
  };

  const formatTime12 = (time24) => {
    const [hrs, mins] = time24.split(':');
    const h = parseInt(hrs);
    const suffix = h >= 12 ? 'PM' : 'AM';
    const displayH = h % 12 || 12;
    return `${displayH}:${mins} ${suffix}`;
  };

  const generateTimeSlots = () => {
    let slotsConfig = doctorInfo?.booking_slots;
    if (typeof slotsConfig === 'string') {
      try { slotsConfig = JSON.parse(slotsConfig); } catch (e) { return []; }
    }
    if (!slotsConfig || !Array.isArray(slotsConfig)) return [];
    
    const slots = [];
    slotsConfig.forEach(range => {
      let current = range.start;
      const end = range.end;
      
      while (current < end) {
        slots.push(current);
        const [h, m] = current.split(':').map(Number);
        let nextM = m + 30;
        let nextH = h;
        if (nextM >= 60) {
          nextM = 0;
          nextH += 1;
        }
        current = `${String(nextH).padStart(2, '0')}:${String(nextM).padStart(2, '0')}`;
      }
    });
    return slots;
  };

  const isSlotPast = (slot) => {
    if (formData.preferred_date !== getLocalDateString(new Date())) return false;
    const now = new Date();
    const [h, m] = slot.split(':').map(Number);
    const slotDate = new Date();
    slotDate.setHours(h, m, 0, 0);
    return slotDate < now;
  };

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 100 : -100,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 100 : -100,
      opacity: 0
    })
  };

  if (isSuccess) {
    return (
      <div className="booking-page success-variant">
        <Navbar />
        <main className="booking-main success-main">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="success-card"
          >
            <div className="success-icon-section">
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', damping: 12, stiffness: 200, delay: 0.2 }}
                className="checkmark-circle"
              >
                <CheckCircle2 size={48} color="white" strokeWidth={3} />
              </motion.div>
            </div>

            <div className="success-body">
              <h1>Booking Confirmed!</h1>
              <p className="success-subtitle">Your home visit has been scheduled</p>

              <div className="booking-ref-card">
                <span className="ref-title">Booking Reference</span>
                <code className="ref-number">{bookingRef}</code>
              </div>

              <div className="success-details">
                <div className="detail-row">
                  <Calendar size={18} />
                  <div>
                    <p className="detail-val">{formatFullDate(formData.preferred_date)}</p>
                    <p className="detail-val">{formatTime12(formData.slot_time)} — {formatTime12((() => {
                      const [h, m] = formData.slot_time.split(':').map(Number);
                      let nm = m + 30;
                      let nh = h;
                      if (nm >= 60) { nm = 0; nh += 1; }
                      return `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
                    })())}</p>
                  </div>
                </div>
                <div className="detail-row">
                  <MapPin size={18} />
                  <p className="detail-val">{formData.address.towerNo}, {formData.address.flatNo}, {formData.address.apartmentName}</p>
                </div>
                <div className="detail-row border-none">
                  <Users size={18} />
                  <p className="detail-val">{formData.individuals.length} person(s) for examination</p>
                </div>
              </div>

              <div className="confirmation-note">
                <div className="note-dot" />
                <p>The doctor will confirm your appointment shortly. You'll receive updates on your phone.</p>
              </div>

              <div className="success-footer">
                <button 
                  className="btn-secondary full-width"
                  onClick={() => window.location.reload()}
                >
                  Book Another
                </button>
                <button 
                  className="btn-link"
                  onClick={() => navigate('/')}
                >
                  Go to Homepage
                </button>
              </div>
            </div>
          </motion.div>
        </main>
      </div>
    );
  }

  return (
    <div className="booking-page">
      <Navbar>
        <div className="step-indicator">
          {STEPS.map((s, idx) => (
            <React.Fragment key={s.id}>
              <div className={`step-circle ${step >= s.id ? 'active' : ''} ${step > s.id ? 'completed' : ''}`}>
                {step > s.id ? <CheckCircle2 size={16} /> : <span>{s.id}</span>}
              </div>
              {idx < STEPS.length - 1 && (
                <div className={`step-line ${step > s.id ? 'active' : ''}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </Navbar>

      <main className="booking-main">
        <div className="booking-container">
          {step === 1 && (
            <div className="booking-page-header">
              <h1 className="main-title">Book Home Visit — Medical Fitness Certificate</h1>
            </div>
          )}
          
          <AnimatePresence mode="wait" custom={direction}>
            {step === 1 && (
              <motion.div
                key="step1"
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3 }}
                className="step-content"
              >
                <div className="phone-step-container">
                  <div className="phone-input-section">
                    <div className="icon-circle">
                      <Phone size={32} />
                    </div>
                    <h3>Enter Your Phone Number</h3>
                    <p>We'll use this to send you booking confirmation</p>
                    
                    <div className="phone-input-group">
                      <span className="prefix">+91</span>
                      <input 
                        type="tel" 
                        placeholder="10-digit mobile number" 
                        value={formData.phone}
                        onChange={handlePhoneChange}
                        autoFocus
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                key="step2"
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3 }}
                className="step-content"
              >
                <div className="address-step-container">
                  <div className="section-header">
                    <MapPin size={24} className="header-icon" />
                    <h2>Visit Address</h2>
                  </div>
                  
                  <div className="info-card">
                    <div className="card-body grid-2">
                      <div className="input-field">
                        <label>Tower / Block *</label>
                        <Building2 className="field-icon" size={18} />
                        <input 
                          type="text" 
                          name="towerNo"
                          value={formData.address.towerNo}
                          onChange={handleAddressChange}
                          placeholder="e.g. Tower A"
                        />
                      </div>
                      
                      <div className="input-field">
                        <label>Flat / Villa No. *</label>
                        <Home className="field-icon" size={18} />
                        <input 
                          type="text" 
                          name="flatNo"
                          value={formData.address.flatNo}
                          onChange={handleAddressChange}
                          placeholder="e.g. 402"
                        />
                      </div>

                      <div className="input-field full-width">
                        <label>Apartment / Society Name *</label>
                        <MapPin className="field-icon" size={18} />
                        <input 
                          type="text" 
                          name="apartmentName"
                          value={formData.address.apartmentName}
                          onChange={handleAddressChange}
                          placeholder="Name of your society or apartment"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div
                key="step3"
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3 }}
                className="step-content"
              >
                <div className="individuals-step-container">
                  <div className="section-header">
                    <Users size={24} className="header-icon" />
                    <h2>People to be Examined</h2>
                  </div>

                  <div className="individuals-list">
                    <AnimatePresence initial={false}>
                      {formData.individuals.map((ind, index) => (
                        <motion.div 
                          key={ind.id}
                          layout
                          initial={{ opacity: 0, scale: 0.9, height: 0 }}
                          animate={{ opacity: 1, scale: 1, height: 'auto' }}
                          exit={{ opacity: 0, scale: 0.9, height: 0 }}
                          transition={{ duration: 0.2 }}
                          className="info-card individual-card"
                        >
                          <div className="card-header">
                            <span className="person-label">Person {index + 1}</span>
                            {index > 0 && (
                              <button 
                                className="btn-remove" 
                                onClick={() => removeIndividual(ind.id)}
                              >
                                <X size={16} />
                              </button>
                            )}
                          </div>
                          <div className="card-body grid-3">
                            <div className="input-field name-field">
                              <label>Full Name *</label>
                              <User className="field-icon" size={18} />
                              <input 
                                type="text" 
                                value={ind.name}
                                onChange={(e) => handleIndividualChange(ind.id, 'name', e.target.value)}
                                placeholder="Full name of person"
                              />
                            </div>
                            <div className="input-field">
                              <label>Age *</label>
                              <Activity className="field-icon" size={18} />
                              <input 
                                type="number" 
                                value={ind.age}
                                onChange={(e) => handleIndividualChange(ind.id, 'age', e.target.value)}
                                placeholder="Age"
                                min="1"
                                max="120"
                              />
                            </div>
                            <div className="input-field">
                              <label>Gender *</label>
                              <Users className="field-icon" size={18} />
                              <select 
                                value={ind.gender}
                                onChange={(e) => handleIndividualChange(ind.id, 'gender', e.target.value)}
                              >
                                <option value="Male">Male</option>
                                <option value="Female">Female</option>
                                <option value="Other">Other</option>
                              </select>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>

                  <button 
                    className="btn-add-person" 
                    onClick={addIndividual}
                    disabled={formData.individuals.length >= 10}
                  >
                    <UserPlus size={20} />
                    <span>Add Another Person</span>
                  </button>

                  <div className="notes-container">
                    <div className="input-field full-width">
                      <label>Additional Notes (optional)</label>
                      <textarea 
                        value={formData.notes}
                        onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="Any special requirements, medical conditions to note, etc."
                        rows={3}
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 4 && (
              <motion.div
                key="step4"
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3 }}
                className="step-content"
              >
                <div className="scheduling-step-container">
                  <div className="section-header">
                    <Calendar size={24} className="header-icon" />
                    <h2>Select Date & Time</h2>
                  </div>

                  <div className="calendar-slots-grid">
                    <div className="info-card calendar-card">
                      <div className="card-header">
                        <span className="header-title">
                          {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </span>
                        <div className="calendar-nav">
                          <button 
                            disabled={currentMonth.getMonth() === new Date().getMonth() && currentMonth.getFullYear() === new Date().getFullYear()}
                            onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                          >
                            <ChevronLeft size={16} />
                          </button>
                          <button 
                            disabled={currentMonth.getMonth() === new Date().getMonth() + 1}
                            onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                          >
                            <ChevronRight size={16} />
                          </button>
                        </div>
                      </div>
                      <div className="calendar-grid">
                        {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map(d => (
                          <div key={d} className="calendar-day-header">{d}</div>
                        ))}
                        {generateCalendarDays().map((date, idx) => {
                          const available = isDayAvailable(date);
                          const selected = isDaySelected(date);
                          const today = date && date.toDateString() === new Date().toDateString();
                          const isWeekend = date && (date.getDay() === 0 || date.getDay() === 6);
                          
                          return (
                            <div 
                              key={date ? date.toISOString() : `empty-${idx}`} 
                              className={`calendar-day ${!date ? 'empty' : ''} ${!available ? 'disabled' : ''} ${selected ? 'selected' : ''} ${isWeekend && available ? 'weekend' : ''}`}
                              onClick={() => available && setFormData(prev => ({ 
                                ...prev, 
                                preferred_date: getLocalDateString(date),
                                slot_time: '' 
                              }))}
                            >
                              {date && (
                                <>
                                  <span className="day-num">{date.getDate()}</span>
                                  {today && <div className="today-dot" />}
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <AnimatePresence mode="wait">
                      {formData.preferred_date ? (
                        <motion.div 
                          key="slots"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          className="slots-container"
                        >
                          <div className="slots-wrapper">
                            <label className="slots-label">
                              Available Slots for {(() => {
                                const [y, m, d] = formData.preferred_date.split('-').map(Number);
                                return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
                              })()}
                            </label>
                            <div className="slots-grid">
                              {generateTimeSlots().length > 0 ? (
                                generateTimeSlots().map(slot => {
                                  const isBooked = bookedSlots.includes(slot);
                                  const isPast = isSlotPast(slot);
                                  const unavailable = isBooked || isPast;
                                  const selected = formData.slot_time === slot;
                                  
                                  return (
                                    <button
                                      key={slot}
                                      className={`slot-btn ${unavailable ? 'unavailable' : ''} ${selected ? 'selected' : ''}`}
                                      disabled={unavailable}
                                      onClick={() => setFormData(prev => ({ ...prev, slot_time: slot }))}
                                    >
                                      <span>{formatTime12(slot)}</span>
                                      {isBooked && <span className="slot-subtext">Booked</span>}
                                      {isPast && <span className="slot-subtext">Past</span>}
                                    </button>
                                  );
                                })
                              ) : (
                                <div className="no-slots">No available slots for this date. Please select another date.</div>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      ) : (
                        <div className="pick-date-prompt info-card">
                          <p>Please select a date to view available time slots</p>
                        </div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 5 && (
              <motion.div
                key="step5"
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3 }}
                className="step-content"
              >
                <div className="review-container">
                  <div className="review-header">
                    <Activity className="section-icon" size={24} />
                    <h3>📋 Review Your Booking</h3>
                    <p>Please verify all details before confirming</p>
                  </div>

                  <div className="review-cards">
                    {/* Contact Card */}
                    <div className="info-card review-card">
                      <div className="card-header">
                        <span className="person-label">Contact Details</span>
                        <button className="btn-edit" onClick={() => setStep(1)}>Edit</button>
                      </div>
                      <div className="card-body">
                        <div className="summary-item">
                          <Phone size={18} className="text-gray-400" />
                          <span>+91 {formData.phone.replace(/(\d{5})(\d{5})/, '$1 $2')}</span>
                        </div>
                      </div>
                    </div>

                    {/* Address Card */}
                    <div className="info-card review-card">
                      <div className="card-header">
                        <span className="person-label">Visit Address</span>
                        <button className="btn-edit" onClick={() => setStep(2)}>Edit</button>
                      </div>
                      <div className="card-body">
                        <div className="summary-item align-start">
                          <MapPin size={18} className="text-gray-400 mt-1" />
                          <div className="address-block">
                            <p className="font-bold">{formData.address.towerNo}, {formData.address.flatNo}</p>
                            <p>{formData.address.apartmentName}</p>
                            <p className="text-gray-500 text-sm">Noida</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* People Card */}
                    <div className="info-card review-card">
                      <div className="card-header">
                        <span className="person-label">People to be Examined</span>
                        <button className="btn-edit" onClick={() => setStep(3)}>Edit</button>
                      </div>
                      <div className="card-body">
                        <div className="people-list">
                          {formData.individuals.map((ind, idx) => (
                            <div key={idx} className="person-summary-row">
                              <span className="row-num">{idx + 1}</span>
                              <span className="row-name">{ind.name}</span>
                              <span className="row-meta">• {ind.age} yrs • {ind.gender}</span>
                            </div>
                          ))}
                        </div>
                        <div className="total-badge">
                          Total: {formData.individuals.length} person(s)
                        </div>
                      </div>
                    </div>

                    {/* Appointment Card */}
                    <div className="info-card review-card">
                      <div className="card-header">
                        <span className="person-label">Appointment Slot</span>
                        <button className="btn-edit" onClick={() => setStep(4)}>Edit</button>
                      </div>
                      <div className="card-body">
                        <div className="summary-item">
                          <Calendar size={18} className="text-gray-400" />
                          <span>{formatFullDate(formData.preferred_date)}</span>
                        </div>
                        <div className="summary-item">
                          <Clock size={18} className="text-gray-400" />
                          <span>{formatTime12(formData.slot_time)} — {formatTime12((() => {
                            const [h, m] = formData.slot_time.split(':').map(Number);
                            let nm = m + 30;
                            let nh = h;
                            if (nm >= 60) { nm = 0; nh += 1; }
                            return `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
                          })())}</span>
                        </div>
                      </div>
                    </div>

                    {/* Notes Card */}
                    {formData.notes && (
                      <div className="info-card review-card">
                        <div className="card-header">
                          <span className="person-label">Additional Notes</span>
                          <button className="btn-edit" onClick={() => setStep(4)}>Edit</button>
                        </div>
                        <div className="card-body">
                          <p className="summary-notes">{formData.notes}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>

      <footer className="booking-footer">
        <div className="footer-container">
          <button className="btn-back" onClick={prevStep} disabled={isSubmitting}>
            <ChevronLeft size={20} />
            <span>{step === 1 ? 'Cancel' : 'Back'}</span>
          </button>
          
          {step === 5 ? (
            <button 
              className="btn-continue btn-confirm" 
              onClick={handleConfirmBooking}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="spinner-small" />
                  <span>Confirming...</span>
                </>
              ) : (
                <>
                  <span>Confirm Booking</span>
                  <CheckCircle2 size={20} />
                </>
              )}
            </button>
          ) : (
            <button 
              className="btn-continue" 
              onClick={nextStep}
              disabled={step === 1 && formData.phone.length !== 10}
            >
              <span>Continue</span>
              <ChevronRight size={20} />
            </button>
          )}
        </div>
      </footer>
    </div>
  );
}

export default BookAppointment;

